import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, KanbanSquare, Plus, Search, Table2, X } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

export type IndexView = { key: string; label: string };

/**
 * The frame every object list sits in — contacts, companies, deals, leads,
 * tickets: a white panel with the object's name and count, view tabs, a
 * toolbar (search, filters, list/board switch) and the table or board below.
 */
export function ObjectIndex({
  title,
  count,
  actions,
  views,
  viewsNode,
  activeView,
  onViewChange,
  search,
  onSearchChange,
  searchPlaceholder = 'Search',
  filters,
  hasFilters,
  onClearFilters,
  mode,
  tableHref,
  boardHref,
  calendarHref,
  toolbarEnd,
  banner,
  children,
}: {
  title: string;
  count?: number;
  actions?: ReactNode;
  views?: IndexView[];
  /** A custom view bar (saved views), drawn instead of `views`. */
  viewsNode?: ReactNode;
  activeView?: string;
  onViewChange?: (key: string) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Filter controls shown in the row under the search box. */
  filters?: ReactNode;
  hasFilters?: boolean;
  onClearFilters?: () => void;
  /** Shows the list/board switch when both links are given. */
  mode?: 'table' | 'board' | 'calendar';
  tableHref?: string;
  boardHref?: string;
  calendarHref?: string;
  toolbarEnd?: ReactNode;
  banner?: ReactNode;
  children: ReactNode;
}) {
  const t = useT();
  const searchRef = useRef<HTMLInputElement>(null);

  // "/" jumps to the search box, as in the big CRMs.
  useEffect(() => {
    if (!onSearchChange) return;
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key !== '/' || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) return;
      event.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onSearchChange]);

  return (
    <div className="rounded-xl border bg-card">
      {banner}
      <div className="px-4 pt-4 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-baseline gap-2.5">
            <h1 className="truncate text-[1.5rem] font-bold leading-tight">{t(title)}</h1>
            {count !== undefined && (
              <span className="tabular rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
                {count.toLocaleString()}
              </span>
            )}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>

        {viewsNode}
        {!viewsNode && views && views.length > 0 && (
          <div className="scrollbar-thin -mb-px mt-3 flex items-end gap-1 overflow-x-auto border-b">
            {views.map((view) => {
              const active = view.key === activeView;
              return (
                <button
                  key={view.key}
                  type="button"
                  onClick={() => onViewChange?.(view.key)}
                  className={cn(
                    'relative flex shrink-0 items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-semibold transition-colors',
                    active ? 'bg-muted/70 text-foreground' : 'text-muted-foreground hover:bg-muted/40 hover:text-foreground',
                  )}
                >
                  <Table2 className="h-3.5 w-3.5 opacity-60" />
                  {t(view.label)}
                  {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-foreground" />}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-5">
        {onSearchChange && (
          <div className="relative w-full sm:w-64">
            <input
              ref={searchRef}
              value={search ?? ''}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={`${t(searchPlaceholder)} ( / )`}
              className="h-9 w-full rounded-full border border-input bg-card pe-9 ps-3.5 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-[3px] focus:ring-primary/15"
            />
            {search ? (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute end-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <Search className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            )}
          </div>
        )}
        {filters}
        {hasFilters && onClearFilters && (
          <button type="button" onClick={onClearFilters} className="text-link text-xs font-semibold hover:underline">
            {t('Clear all')}
          </button>
        )}
        <div className="ms-auto flex items-center gap-2">
          {toolbarEnd}
          {tableHref && boardHref && (
            <div className="flex rounded-md border p-0.5">
              <ModeLink to={tableHref} active={mode === 'table'} label={t('Table')}>
                <Table2 className="h-4 w-4" />
              </ModeLink>
              <ModeLink to={boardHref} active={mode === 'board'} label={t('Board')}>
                <KanbanSquare className="h-4 w-4" />
              </ModeLink>
              {calendarHref && (
                <ModeLink to={calendarHref} active={mode === 'calendar'} label={t('Calendar')}>
                  <CalendarDays className="h-4 w-4" />
                </ModeLink>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-t">{children}</div>
    </div>
  );
}

function ModeLink({ to, active, label, children }: { to: string; active: boolean; label: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      title={label}
      aria-label={label}
      className={cn(
        'flex h-7 w-8 items-center justify-center rounded transition-colors',
        active ? 'bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {children}
    </Link>
  );
}

/**
 * A filter as a pill, the way HubSpot shows them: the label, and the chosen
 * value once there is one. Wraps a native select, so it stays accessible.
 */
export function FilterPill({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  const t = useT();
  const chosen = options.find((option) => option.value === value);
  return (
    <label
      className={cn(
        'relative inline-flex h-8 cursor-pointer items-center gap-1 rounded-full border px-3 text-[13px] font-semibold transition-colors',
        value ? 'border-primary/40 bg-primary/[0.07] text-foreground' : 'text-foreground/80 hover:bg-accent',
      )}
    >
      <span>{t(label)}</span>
      {chosen && value && <span className="max-w-[10rem] truncate font-normal text-muted-foreground">: {chosen.label}</span>}
      <svg viewBox="0 0 10 6" className="ms-0.5 h-1.5 w-2.5 fill-current opacity-60" aria-hidden>
        <path d="M0 0h10L5 6z" />
      </svg>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
        aria-label={t(label)}
      >
        <option value="">{t('Any')}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Saved views for a list, kept per person in the browser. */
export function useSavedViews(storageKey: string, builtIn: IndexView[]) {
  const [active, setActive] = useState<string>(() => {
    try {
      return localStorage.getItem(`dovo.view.${storageKey}`) ?? builtIn[0]?.key ?? '';
    } catch {
      return builtIn[0]?.key ?? '';
    }
  });
  const choose = (key: string) => {
    setActive(key);
    try {
      localStorage.setItem(`dovo.view.${storageKey}`, key);
    } catch {
      // Not remembered.
    }
  };
  return { views: builtIn, active: builtIn.some((view) => view.key === active) ? active : (builtIn[0]?.key ?? ''), choose };
}

/** The coral primary button with a plus, used for "Add contact" and friends. */
export function AddButton({ onClick, label }: { onClick: () => void; label: string }) {
  const t = useT();
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
    >
      <Plus className="h-4 w-4" />
      {t(label)}
    </button>
  );
}
